import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { businessesApi } from "@/api/counterparties";
import type {
  BusinessDetailDto,
  CreateBusinessRequest,
  DedupCandidateDto,
  DedupCheckResultDto,
  UpdateBusinessRequest,
} from "@/types/counterparties";
import { Button } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { useToast } from "@/components/ui/Toast";
import {
  BusinessForm,
  EMPTY_BUSINESS_FORM,
  type BusinessFormState,
} from "@/components/counterparties/BusinessForm";
import { DedupCheckModal } from "@/components/counterparties/DedupCheckModal";
import {
  CriticalFieldEditConfirmModal,
  type CriticalFieldChange,
} from "@/components/counterparties/CriticalFieldEditConfirmModal";
import { businessKeys, invalidateCounterpartyReads } from "../counterpartyKeys";

export function BusinessEditPage() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [form, setForm] = useState<BusinessFormState>(EMPTY_BUSINESS_FORM);
  const [editingEin, setEditingEin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dedupOpen, setDedupOpen] = useState(false);
  const [dedupResult, setDedupResult] = useState<DedupCheckResultDto | null>(null);
  const [confirmCritical, setConfirmCritical] = useState<{
    changes: CriticalFieldChange[];
    onConfirm: () => void;
  } | null>(null);

  const existing = useQuery({
    queryKey: businessKeys.detail(id),
    queryFn: () => businessesApi.get(id!),
    enabled: editing,
  });

  useBreadcrumbs(
    editing
      ? existing.data
        ? [
            { label: "Businesses", href: "/counterparties/businesses" },
            { label: existing.data.legalEntityName, href: `/counterparties/businesses/${id}` },
            { label: "Edit" },
          ]
        : null
      : [
          { label: "Businesses", href: "/counterparties/businesses" },
          { label: "New business" },
        ],
  );

  useEffect(() => {
    if (existing.data) {
      setForm(businessDetailToFormState(existing.data));
      setEditingEin(false);
    }
  }, [existing.data]);

  const create = useMutation({
    mutationFn: (body: CreateBusinessRequest) => businessesApi.create(body),
    onSuccess: (created) => {
      invalidateCounterpartyReads(queryClient, "business");
      toast.success(`Business ${created.legalEntityName} created.`);
      navigate(`/counterparties/businesses/${created.id}`);
    },
    onError: (e: Error) => setError(e.message),
  });

  const update = useMutation({
    mutationFn: (body: UpdateBusinessRequest) => businessesApi.update(id!, body),
    onSuccess: (updated) => {
      invalidateCounterpartyReads(queryClient, "business");
      toast.success("Business updated.");
      navigate(`/counterparties/businesses/${updated.id}`);
    },
    onError: (e: Error) => setError(e.message),
  });

  const dedupRequest = useMemo(() => ({
    legalEntityName: form.legalEntityName || null,
    dba: form.dba || null,
    stateOfFormation: form.stateOfFormation || null,
    ein: editingEin || !editing ? form.ein ?? null : null,
    naicsCode: form.naicsCode || null,
    hqCity: form.hqAddress.city || null,
  }), [form, editingEin, editing]);

  // Run the duplicate check up front. The modal is only shown when a
  // candidate match exists; with no match we create the record directly.
  const dedupPrecheck = useMutation({
    mutationFn: () => businessesApi.dedupCheck(dedupRequest),
    onSuccess: (result) => {
      if (result.strength === "None") {
        create.mutate({ ...form, confirmCreateNew: true, createNewReason: null });
        return;
      }
      setDedupResult(result);
      setDedupOpen(true);
    },
    onError: (e: Error) => setError(e.message),
  });

  const onSave = () => {
    setError(null);
    if (!form.legalEntityName || !form.naicsCode || !form.industryDescription || !form.stateOfFormation) {
      setError("Please fill all required fields (legal name, state of formation, NAICS, industry).");
      return;
    }
    if (!editing) {
      runCreate();
      return;
    }
    const changes = detectCriticalFieldChanges(existing.data, form, editingEin);
    if (changes.length > 0) {
      setConfirmCritical({
        changes,
        onConfirm: () => { setConfirmCritical(null); submitUpdate(); },
      });
      return;
    }
    submitUpdate();
  };

  const runCreate = (confirmCreateNew = false, reason: string | null = null) => {
    if (!confirmCreateNew) {
      dedupPrecheck.mutate();
      return;
    }
    create.mutate({ ...form, confirmCreateNew, createNewReason: reason });
  };

  const submitUpdate = () => {
    if (!existing.data) return;
    update.mutate({ ...form, status: existing.data.status });
  };

  const isPending = create.isPending || update.isPending || dedupPrecheck.isPending;
  const requiredComplete =
    !!form.legalEntityName &&
    !!form.stateOfFormation &&
    !!form.naicsCode &&
    !!form.industryDescription;

  return (
    <>
      <PageHeader
        eyebrow="Counterparties · Business"
        title={editing ? "Edit business" : "New business"}
        description={
          editing
            ? "Edit identity, industry, operations, and HQ details. Critical-field changes are confirmed."
            : undefined
        }
        actions={
          <>
            <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
            <Button size="sm" onClick={onSave} loading={isPending} disabled={!requiredComplete}>
              <Save size={14} strokeWidth={1.5} aria-hidden /> {editing ? "Save changes" : "Save Business"}
            </Button>
          </>
        }
      />

      {error && (
        <div className="bg-status-error/10 border-l-[3px] border-status-error text-small text-status-error p-space-3 rounded-md mb-space-3" role="alert">
          {error}
        </div>
      )}

      <BusinessForm
        value={form}
        onChange={setForm}
        einMasked={existing.data?.einMasked}
        editingEin={editingEin}
        onEditEin={() => setEditingEin(true)}
      />

      <DedupCheckModal
        open={dedupOpen}
        kind="Business"
        request={dedupRequest}
        result={dedupResult ?? undefined}
        onLinkExisting={(c: DedupCandidateDto) => {
          setDedupOpen(false);
          toast.info(`Linked to existing business ${c.displayName}.`);
          navigate(`/counterparties/businesses/${c.id}`);
        }}
        onCreateNew={(reason) => {
          setDedupOpen(false);
          runCreate(true, reason || null);
        }}
        onCancel={() => {
          setDedupOpen(false);
          setDedupResult(null);
        }}
      />

      {confirmCritical && (
        <CriticalFieldEditConfirmModal
          open
          changes={confirmCritical.changes}
          onConfirm={confirmCritical.onConfirm}
          onCancel={() => setConfirmCritical(null)}
        />
      )}
    </>
  );
}

function businessDetailToFormState(b: BusinessDetailDto): BusinessFormState {
  return {
    legalEntityName: b.legalEntityName,
    dba: b.dba ?? "",
    entityType: b.entityType,
    stateOfFormation: b.stateOfFormation,
    dateOfFormation: b.dateOfFormation ?? null,
    ein: null,
    naicsCode: b.naicsCode,
    industryDescription: b.industryDescription,
    yearsInOperation: b.yearsInOperation ?? null,
    numberOfLocations: b.numberOfLocations ?? null,
    numberOfEmployees: b.numberOfEmployees ?? null,
    annualRevenuePreliminary: b.annualRevenuePreliminary ?? null,
    annualEBITDAPreliminary: b.annualEBITDAPreliminary ?? null,
    recurringRevenuePct: b.recurringRevenuePct ?? null,
    customerConcentrationTop5Pct: b.customerConcentrationTop5Pct ?? null,
    leaseOrOwn: b.leaseOrOwn ?? null,
    leaseExpirationDate: b.leaseExpirationDate ?? null,
    hqAddress: b.hqAddress,
    additionalLocationsJson: b.additionalLocationsJson ?? null,
    website: b.website ?? "",
    briefDescription: b.briefDescription ?? "",
    tags: b.tags,
  };
}

function detectCriticalFieldChanges(
  before: BusinessDetailDto | undefined,
  after: BusinessFormState,
  editingEin: boolean,
): CriticalFieldChange[] {
  if (!before) return [];
  const changes: CriticalFieldChange[] = [];
  if (before.legalEntityName !== after.legalEntityName) {
    changes.push({
      field: "Legal entity name",
      before: before.legalEntityName,
      after: after.legalEntityName,
    });
  }
  if (editingEin && (after.ein ?? "").length > 0) {
    changes.push({
      field: "EIN",
      before: before.einMasked ?? "—",
      after: "**-***" + (after.ein ?? "").slice(-4),
    });
  }
  return changes;
}
